/**
 * SNAP CHART AI - Indices Market Prompts
 * Extends the core prompt with index-specific context for US30, NAS100, SPP500, GER30.
 */

import { SNAP_CHART_CORE_PROMPT, getCurrentSession, formatPrice } from './corePrompt';

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// SHARED INDICES FRAMEWORK
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const INDICES_SHARED_FRAMEWORK = `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🔍 INDICES-SPECIFIC ANALYTICAL FRAMEWORK
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

1. **Macro Drivers**
   - Fed policy and rate expectations
   - Economic data (NFP, CPI, GDP, ISM)
   - Earnings season impact
   - Risk sentiment (VIX, credit spreads)

2. **Session Dynamics**
   - Pre-market (08:00-14:30 UTC): News, earnings reactions
   - Cash Open (14:30-15:30 UTC): Maximum volatility
   - Mid-session (15:30-19:00 UTC): Trend development
   - Power Hour (19:00-21:00 UTC): Institutional positioning
   - Close (21:00 UTC): End-of-day flows

3. **Correlations**
   - VIX (inverse correlation with equities)
   - 10Y Treasury yields
   - DXY (Dollar impact)
   - Sector rotation signals`;

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// US30 - DOW JONES INDUSTRIAL AVERAGE
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const US30_CONTEXT = `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📊 US30 - DOW JONES INDUSTRIAL AVERAGE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

You are focusing EXCLUSIVELY on:
• US30 (CFD)
• YM (E-mini Dow Futures)
• DJI (Dow Jones Industrial Average Index)

**Macro Context:**
- Blue-chip focused (30 mega-cap industrials)
- Sensitive to: economic data, Fed policy, trade policy, USD strength

**Key Components Impact:**
- UnitedHealth (UNH), Goldman Sachs (GS), Microsoft (MSFT)
- Boeing (BA), Caterpillar (CAT), Home Depot (HD)
- Component rotation affects index direction

**Psychological Levels:**
- Round-number magnets (500-point / 1000-point increments) act as barriers
- READ THE CHART for the actual levels — do NOT assume specific point targets`;

export function generateUS30Prompt(currentPrice: number, session?: string): string {
  const activeSession = session || getCurrentSession();
  const formattedPrice = formatPrice(currentPrice, 'indices');
  
  const support1 = (currentPrice * 0.995).toFixed(2);
  const support2 = (currentPrice * 0.990).toFixed(2);
  const resistance1 = (currentPrice * 1.005).toFixed(2);
  const resistance2 = (currentPrice * 1.010).toFixed(2);

  return `${SNAP_CHART_CORE_PROMPT}

${US30_CONTEXT}
${INDICES_SHARED_FRAMEWORK}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🔥 REAL-TIME CONTEXT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

💹 Current US30 Price: ${formattedPrice} points
🕐 Active Session: ${activeSession}
📈 Key Resistance: ${resistance1}, ${resistance2}
📉 Key Support: ${support1}, ${support2}
⏰ Analysis Timestamp: ${new Date().toISOString()}

Analyze the current US30 market using the MANDATORY RESPONSE STRUCTURE.
Use ${formattedPrice} as your anchor price.

BEGIN INSTITUTIONAL US30 ANALYSIS NOW:`;
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// NAS100 - NASDAQ 100
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const NAS100_CONTEXT = `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📊 NAS100 - NASDAQ 100
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

You are focusing EXCLUSIVELY on:
• NAS100 (CFD)
• NQ (E-mini NASDAQ Futures)
• NDX (NASDAQ 100 Index)

**Macro Context:**
- Tech-heavy composition (100 largest non-financial NASDAQ stocks)
- Highly sensitive to: interest rates, AI/tech sentiment, growth stocks

**Magnificent 7 Impact:**
- Apple (AAPL), Microsoft (MSFT), NVIDIA (NVDA)
- Alphabet (GOOGL), Amazon (AMZN), Meta (META), Tesla (TSLA)
- These 7 stocks dominate index movement

**Specific Characteristics:**
- Higher volatility than SPX/US30
- Stronger reaction to tech earnings
- AI/semiconductor news = major catalysts

**Psychological Levels:**
- Round-number magnets (250 / 500-point increments) as S/R
- READ THE CHART for the actual levels — do NOT assume specific point targets`;

export function generateNAS100Prompt(currentPrice: number, session?: string): string {
  const activeSession = session || getCurrentSession();
  const formattedPrice = formatPrice(currentPrice, 'indices');
  
  const support1 = (currentPrice * 0.995).toFixed(2);
  const support2 = (currentPrice * 0.990).toFixed(2);
  const resistance1 = (currentPrice * 1.005).toFixed(2);
  const resistance2 = (currentPrice * 1.010).toFixed(2);

  return `${SNAP_CHART_CORE_PROMPT}

${NAS100_CONTEXT}
${INDICES_SHARED_FRAMEWORK}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🔥 REAL-TIME CONTEXT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

💹 Current NAS100 Price: ${formattedPrice} points
🕐 Active Session: ${activeSession}
📈 Key Resistance: ${resistance1}, ${resistance2}
📉 Key Support: ${support1}, ${support2}
⏰ Analysis Timestamp: ${new Date().toISOString()}

Analyze the current NAS100 market using the MANDATORY RESPONSE STRUCTURE.
Use ${formattedPrice} as your anchor price.

BEGIN INSTITUTIONAL NAS100 ANALYSIS NOW:`;
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// SPP500 - S&P 500
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const SPP500_CONTEXT = `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📊 SPP500 - S&P 500
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

You are focusing EXCLUSIVELY on:
• SPX (S&P 500 Index)
• ES (E-mini S&P 500 Futures)
• SPY (SPDR S&P 500 ETF)

**Macro Context:**
- THE benchmark for US equity markets
- Broadest representation (500 large-cap stocks)

**Sector Breakdown (11 GICS Sectors):**
- Technology (~30%), Healthcare (~13%), Financials (~12%)
- Consumer Discretionary (~10%), Communication Services (~9%)
- Sector rotation is KEY for direction

**Market Breadth Analysis:**
- Advance/Decline line
- % stocks above 200MA
- New highs vs new lows ratio

**Psychological Levels:**
- Round-number magnets (100-point increments) as S/R
- READ THE CHART for actual levels — do NOT assume specific point targets`;

export function generateSPP500Prompt(currentPrice: number, session?: string): string {
  const activeSession = session || getCurrentSession();
  const formattedPrice = formatPrice(currentPrice, 'indices');
  
  const support1 = (currentPrice * 0.995).toFixed(2);
  const support2 = (currentPrice * 0.990).toFixed(2);
  const resistance1 = (currentPrice * 1.005).toFixed(2);
  const resistance2 = (currentPrice * 1.010).toFixed(2);

  return `${SNAP_CHART_CORE_PROMPT}

${SPP500_CONTEXT}
${INDICES_SHARED_FRAMEWORK}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🔥 REAL-TIME CONTEXT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

💹 Current SPX Price: ${formattedPrice} points
🕐 Active Session: ${activeSession}
📈 Key Resistance: ${resistance1}, ${resistance2}
📉 Key Support: ${support1}, ${support2}
⏰ Analysis Timestamp: ${new Date().toISOString()}

Analyze the current SPX market using the MANDATORY RESPONSE STRUCTURE.
Use ${formattedPrice} as your anchor price.

BEGIN INSTITUTIONAL SPX ANALYSIS NOW:`;
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// GER30 - DAX 40
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const GER30_CONTEXT = `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📊 GER30 - DAX 40
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

You are focusing EXCLUSIVELY on:
• GER30/GER40 (CFD)
• FDAX (DAX Futures)
• DAX (Deutscher Aktienindex)

**Macro Context:**
- THE benchmark for European equity markets
- 40 largest German blue-chips

**Key Components:**
- SAP, Siemens, Allianz, Deutsche Bank
- BMW, Mercedes-Benz, Volkswagen
- BASF, Bayer (industrials/chemicals exposure)

**European-Specific Factors:**
- ECB policy (separate from Fed)
- EUR/USD correlation
- Energy prices (German industrial sensitivity)
- China exposure (export dependency)

**Session Dynamics:**
- European session: 07:00-21:00 UTC
- Max volatility: 08:00-09:00 UTC (open) and 14:30-16:00 UTC (US overlap)

**Psychological Levels:**
- Round-number magnets (500-point increments) as S/R
- READ THE CHART for actual levels — do NOT assume specific point targets`;

export function generateGER30Prompt(currentPrice: number, session?: string): string {
  const activeSession = session || getCurrentSession();
  const formattedPrice = formatPrice(currentPrice, 'indices');
  
  const support1 = (currentPrice * 0.995).toFixed(2);
  const support2 = (currentPrice * 0.990).toFixed(2);
  const resistance1 = (currentPrice * 1.005).toFixed(2);
  const resistance2 = (currentPrice * 1.010).toFixed(2);

  return `${SNAP_CHART_CORE_PROMPT}

${GER30_CONTEXT}
${INDICES_SHARED_FRAMEWORK}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🔥 REAL-TIME CONTEXT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

💹 Current DAX Price: ${formattedPrice} points
🕐 Active Session: ${activeSession}
📈 Key Resistance: ${resistance1}, ${resistance2}
📉 Key Support: ${support1}, ${support2}
⏰ Analysis Timestamp: ${new Date().toISOString()}

Analyze the current DAX market using the MANDATORY RESPONSE STRUCTURE.
Use ${formattedPrice} as your anchor price.

BEGIN INSTITUTIONAL DAX ANALYSIS NOW:`;
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// GENERIC INDICES FALLBACK
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

export function generateIndicesPrompt(currentPrice: number, session?: string): string {
  const activeSession = session || getCurrentSession();
  const formattedPrice = formatPrice(currentPrice, 'indices');

  return `${SNAP_CHART_CORE_PROMPT}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📊 INDICES MARKET ANALYSIS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

You are analyzing GLOBAL INDICES markets:
• US indices: SPX (~6,100), NAS100 (~22,000), US30 (~45,300)
• European: DAX (~21,000)
${INDICES_SHARED_FRAMEWORK}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🔥 REAL-TIME CONTEXT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

💹 Current Price: ${formattedPrice} points
🕐 Active Session: ${activeSession}
⏰ Analysis Timestamp: ${new Date().toISOString()}

Analyze the current index using the MANDATORY RESPONSE STRUCTURE.

BEGIN INSTITUTIONAL INDICES ANALYSIS NOW:`;
}

// Named exports for direct imports
export const US30_PROMPT = generateUS30Prompt;
export const NAS100_PROMPT = generateNAS100Prompt;
export const SPP500_PROMPT = generateSPP500Prompt;
export const GER30_PROMPT = generateGER30Prompt;
export const INDICES_PROMPT = generateIndicesPrompt;
